// src/features/players/components/SecurityTab.tsx

import { useState, useMemo, useCallback } from 'react';
import {
  Card, Group, Text, Button, Stack, Box, TextInput, Select, Switch, Divider,
  Badge, ActionIcon, Tooltip, PasswordInput,
} from '@mantine/core';
import { IconPlus, IconX } from '@tabler/icons-react';
import { useForm, useController, type Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { notifications } from '@mantine/notifications';
import { securityFormSchema } from '../schemas/PlayerForm.schema';
import { useUpdatePlayer } from '../usePlayerMutations';
import { PlayerUtils } from '../PlayerUtils';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { GeoFencingSection } from './GeoFencingSection';
import {
  PLAYER_CONSTRAINTS,
  ACCESS_CONTROL_TYPES,
  ACL_GEO_RULE_TYPES,
  ACL_GEO_RULE_LABELS,
  type AclGeoRuleTypeValue,
} from '@/lib/constants/player';
import type { PlayerViewData, PlayerSecurityFormData, IAclRuleData } from '@/lib/types/Player';

interface SecurityTabProps {
  player: PlayerViewData;
}

export function SecurityTab({ player }: SecurityTabProps) {
  const playerId = player.core.playerId;
  const updateMutation = useUpdatePlayer(playerId);
  const { selectedCustomer } = useAuth();
  const allowFeed = selectedCustomer?.cfg.quota.features.account.allowFeed === true;

  // Form handles password fields only
  const { control, handleSubmit, formState: { isDirty: formIsDirty, errors }, reset, watch } = useForm<PlayerSecurityFormData>({
    resolver: zodResolver(securityFormSchema),
    defaultValues: PlayerUtils.transformToSecurityForm(player),
  });

  const accessControlType = watch('accessControlType');

  // Domain ACLs — managed as state (not RHF)
  const initialDomainAcls = useMemo(
    () => player.acls.filter(a => a.aclRuleType === 'domain'),
    [player.acls],
  );
  const [domainAcls, setDomainAcls] = useState<IAclRuleData[]>(initialDomainAcls);
  const [domainInput, setDomainInput] = useState('');

  // Geo-fencing ACLs — managed as state per type
  const initialGeoAcls = useMemo(() => {
    const map: Record<AclGeoRuleTypeValue, IAclRuleData[]> = {
      geo_country: [],
      geo_region: [],
      geo_dma: [],
      geo_postalcode: [],
    };
    for (const acl of player.acls) {
      if (acl.aclRuleType in map) {
        map[acl.aclRuleType as AclGeoRuleTypeValue].push(acl);
      }
    }
    return map;
  }, [player.acls]);

  const [geoAcls, setGeoAcls] = useState(initialGeoAcls);

  const handleGeoAclsChange = useCallback((ruleType: AclGeoRuleTypeValue, acls: IAclRuleData[]) => {
    setGeoAcls(prev => ({ ...prev, [ruleType]: acls }));
  }, []);

  // Track whether any ACLs have changed
  const aclsChanged = useMemo(() => {
    const currentAll = [...domainAcls, ...Object.values(geoAcls).flat()];
    const originalAll = player.acls;
    if (currentAll.length !== originalAll.length) return true;
    return JSON.stringify(currentAll.sort(aclSort)) !== JSON.stringify(originalAll.sort(aclSort));
  }, [domainAcls, geoAcls, player.acls]);

  const isDirty = formIsDirty || aclsChanged;

  // Domain management
  const handleAddDomain = () => {
    const value = domainInput.trim().toLowerCase();
    if (!value) return;
    const exists = domainAcls.some(a => a.value.toLowerCase() === value);
    if (exists) {
      notifications.show({ title: 'Duplicate', message: `"${value}" is already in the list`, color: 'yellow' });
      return;
    }
    setDomainAcls(prev => [...prev, { aclRuleType: 'domain' as IAclRuleData['aclRuleType'], isDeny: false, value }]);
    setDomainInput('');
  };

  const handleRemoveDomain = (value: string) => {
    setDomainAcls(prev => prev.filter(a => a.value.toLowerCase() !== value.toLowerCase()));
  };

  // Save merges form + domain ACLs + geo ACLs
  const onSubmit = async (data: PlayerSecurityFormData) => {
    try {
      const allAcls = [
        ...domainAcls,
        ...geoAcls.geo_country,
        ...geoAcls.geo_region,
        ...geoAcls.geo_dma,
        ...geoAcls.geo_postalcode,
      ];
      const req = PlayerUtils.transformSecurityFormToUpdate(data, player, allAcls);
      await updateMutation.mutateAsync(req);
      reset(data);
      notifications.show({ title: 'Saved', message: 'Security settings updated', color: 'green' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save changes';
      notifications.show({ title: 'Error', message, color: 'red' });
    }
  };

  const onInvalid = (fieldErrors: typeof errors) => {
    const firstError = Object.values(fieldErrors).find(e => e?.message);
    if (firstError?.message) {
      notifications.show({ title: 'Validation Error', message: firstError.message, color: 'orange' });
    }
  };

  const handleDiscard = () => {
    reset();
    setDomainAcls(initialDomainAcls);
    setGeoAcls(initialGeoAcls);
  };

  const isPending = updateMutation.isPending;

  return (
    <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
      <Stack gap="xl" maw={800}>
        <Box
          pos="sticky"
          top="calc(var(--app-shell-header-height, 60px) + var(--mantine-spacing-md))"
          bg="white"
          py="sm"
          style={{ zIndex: 10, borderBottom: '1px solid var(--mantine-color-gray-2)' }}
        >
          <Group justify="space-between">
            <Text fw={600} size="lg">Security & Access</Text>
            <Group gap="sm">
              {isDirty && (
                <Button variant="default" onClick={handleDiscard} disabled={isPending}>
                  Discard
                </Button>
              )}
              <Button type="submit" disabled={!isDirty} loading={isPending}>
                Save Changes
              </Button>
            </Group>
          </Group>
        </Box>

        <Card shadow="sm" padding="lg" withBorder>
          <Stack gap="md">
            {/* Access Control */}
            <Text fw={600} size="sm" c="dimmed" tt="uppercase">Access Control</Text>
            <AccessControlTypeField control={control} />
            {accessControlType === 'password' && <PasswordField control={control} />}

            {/* Domain Restrictions */}
            <Divider label="Domain Restrictions" labelPosition="left" />
            <Text size="sm" c="dimmed">
              Restrict playback to specific domains. Only the listed domains will be allowed.
            </Text>

            <Group gap="xs" align="flex-end">
              <TextInput
                label="Add Domain"
                placeholder="example.com"
                value={domainInput}
                onChange={(e) => setDomainInput(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddDomain();
                  }
                }}
                style={{ flex: 1 }}
              />
              <ActionIcon
                variant="light"
                size="lg"
                onClick={handleAddDomain}
                disabled={!domainInput.trim()}
              >
                <IconPlus size={16} />
              </ActionIcon>
            </Group>

            {domainAcls.length > 0 && (
              <Group gap="xs" wrap="wrap">
                {domainAcls.map(acl => (
                  <Badge
                    key={acl.value}
                    variant="light"
                    size="lg"
                    rightSection={
                      <Tooltip label="Remove" openDelay={300}>
                        <ActionIcon variant="transparent" size="xs" onClick={() => handleRemoveDomain(acl.value)}>
                          <IconX size={12} />
                        </ActionIcon>
                      </Tooltip>
                    }
                  >
                    {acl.value}
                  </Badge>
                ))}
              </Group>
            )}

            {domainAcls.length > 0 && (
              <Text size="xs" c="dimmed">
                {domainAcls.length} domain{domainAcls.length !== 1 ? 's' : ''} allowed
              </Text>
            )}

            {/* Geo-Fencing */}
            {allowFeed && (
              <>
                <Divider label="Geo-Fencing" labelPosition="left" />
                <Text size="sm" c="dimmed">
                  Restrict playback by geographic location.
                </Text>

                {ACL_GEO_RULE_TYPES.map(ruleType => (
                  <GeoFencingSection
                    key={ruleType}
                    ruleType={ruleType}
                    acls={geoAcls[ruleType]}
                    onAclsChange={(acls) => handleGeoAclsChange(ruleType, acls)}
                  />
                ))}
              </>
            )}
          </Stack>
        </Card>
      </Stack>
    </form>
  );
}

// --- Field components ---

function AccessControlTypeField({ control }: { control: Control<PlayerSecurityFormData> }) {
  const { field } = useController({ control, name: 'accessControlType' });
  return (
    <Select
      label="Access Control"
      value={field.value}
      onChange={(val) => val && field.onChange(val)}
      data={[
        { value: 'none', label: 'None (Open Access)' },
        { value: 'password', label: 'Password Protected' },
      ]}
      w={250}
    />
  );
}

function PasswordField({ control }: { control: Control<PlayerSecurityFormData> }) {
  const { field, fieldState } = useController({ control, name: 'password' });
  return (
    <PasswordInput
      label="Password"
      required
      value={field.value}
      onChange={field.onChange}
      onBlur={field.onBlur}
      error={fieldState.error?.message}
      description={`${field.value.length}/${PLAYER_CONSTRAINTS.PASSWORD_MAX}`}
      maxLength={PLAYER_CONSTRAINTS.PASSWORD_MAX}
      w={300}
    />
  );
}

// --- Helpers ---

function aclSort(a: IAclRuleData, b: IAclRuleData): number {
  return `${a.aclRuleType}:${a.value}`.localeCompare(`${b.aclRuleType}:${b.value}`);
}
